import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { FaTimes } from "react-icons/fa";
import useScrollLock from "../../hooks/useScrollLock";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideOutDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: ${(props) =>
    props.$isClosing
      ? css`
          ${fadeOut} 0.3s ease forwards
        `
      : css`
          ${fadeIn} 0.3s ease
        `};
`;

const ModalContainer = styled.div`
  background: var(--surface, #ffffff);
  border-radius: 25px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  animation: ${(props) =>
    props.$isClosing
      ? css`
          ${slideOutDown} 0.3s ease forwards
        `
      : css`
          ${slideInUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)
        `};
  border: 2px solid var(--accent-light);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 30px 30px 20px 30px;

  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 20px;
  color: var(--text-muted);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--accent-light);
    color: var(--surface);
    transform: rotate(90deg);
  }
`;

const ModalTitle = styled.h2`
  margin: 0 40px 0 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);

  text-transform: uppercase;
  background: var(--gradient-primary);
  letter-spacing: 1px;

  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ModalSubtitle = styled.p`
  margin: 15px 0 0 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 20px 30px 30px 30px;
  display: flex;
  gap: 15px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 15px 25px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  letter-spacing: 0.5px;

  ${(props) =>
    props.$variant === "primary"
      ? css`
          background: var(--gradient-primary);
          color: var(--surface);
          border: none;
          box-shadow: var(--shadow-soft);

          &:hover {
            transform: translateY(-3px);
            box-shadow: var(--shadow-medium);
            background: var(--accent-dark);
            color: var(--surface);
          }
        `
      : css`
          background: transparent;
          color: var(--accent-dark);
          border: 2px solid var(--accent-dark);
          &:hover {
            background: var(--accent-dark);
            border: 2px solid transparent;
            color: var(--surface);
            transform: translateY(-3px);
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

// Modal Component
const Modal = ({
  isOpen,
  onClose,
  title = "Modal Title",
  subtitle = "",
  leftButtonText = "Otkaži",
  rightButtonText = "Potvrdi",
  onLeftButtonClick,
  onRightButtonClick,
  showButtons = true,
  showCloseButton = true,
  closeOnOverlayClick = true,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  useScrollLock(isOpen);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      handleClose();
    }
  };

  const handleLeftButton = () => {
    if (onLeftButtonClick) {
      onLeftButtonClick();
    } else {
      handleClose();
    }
  };

  const handleRightButton = () => {
    if (onRightButtonClick) {
      onRightButtonClick();
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        onClose();
      }, 300);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <ModalOverlay $isClosing={isClosing} onClick={handleOverlayClick}>
      <ModalContainer $isClosing={isClosing}>
        <ModalHeader>
          {showCloseButton && (
            <CloseButton onClick={handleClose}>
              <FaTimes />
            </CloseButton>
          )}
          <ModalTitle>{title}</ModalTitle>
          {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
        </ModalHeader>

        {showButtons && (
          <ModalFooter>
            <ModalButton $variant="secondary" onClick={handleLeftButton}>
              {leftButtonText}
            </ModalButton>
            <ModalButton $variant="primary" onClick={handleRightButton}>
              {rightButtonText}
            </ModalButton>
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default Modal;
